import { alpha, Box } from '@mui/material';
import SliderFilter from 'components/shared/SliderFilter';
import { useMemo, useCallback, useEffect, memo } from 'react';
import { PositionLeg } from 'types/optionsCalculator';
import {
  dayjs,
  ET,
  MILLISECONDS_PER_HOUR,
  MILLISECONDS_PER_DAY,
} from 'util/shared';
import { formatPnlTteDateDisplay } from 'util/optionsCalculator';
import { DEFAULT_DTE_PNL_TICK_CONFIG } from 'config/optionsCalculator';

interface BaseDateSettingsProps {
  legs: PositionLeg[];
  baseDate: number;
  updateBaseDate: (baseDate: number) => void;
}

const BaseDateSettings = memo(
  ({ legs, baseDate, updateBaseDate }: BaseDateSettingsProps) => {
    const nowET = useMemo(() => dayjs().tz(ET), []);

    const earliestLegExpiration = useMemo(() => {
      if (legs.length === 0) {
        return 0;
      }
      return Math.min(...legs.map((leg) => leg.expiry));
    }, [legs]);

    // Reset base date if it's after the earliest leg expiration (happens when new position selected)
    useEffect(() => {
      if (earliestLegExpiration > 0 && baseDate > earliestLegExpiration) {
        updateBaseDate(earliestLegExpiration);
      }
    }, [baseDate, earliestLegExpiration, updateBaseDate]);

    // Calculate marks based on time to expiration
    const marks = useMemo(() => {
      if (earliestLegExpiration === 0) {
        return [];
      }

      const marksArray: number[] = [];

      // Always start with current time
      marksArray.push(nowET.valueOf());

      let currentTime = nowET
        .hour(9)
        .minute(30)
        .second(0)
        .millisecond(0)
        .valueOf();

      // Get sorted DTE thresholds (descending order)
      const dteThresholds = [...DEFAULT_DTE_PNL_TICK_CONFIG.keys()].sort(
        (a, b) => b - a,
      );

      while (currentTime < earliestLegExpiration) {
        // Calculate current DTE
        const currentDTE =
          (earliestLegExpiration - currentTime) / MILLISECONDS_PER_DAY;

        // Find appropriate step size based on current DTE
        let stepHours = 0.5; // Default to 30 minutes
        for (const threshold of dteThresholds) {
          if (currentDTE >= threshold) {
            stepHours = DEFAULT_DTE_PNL_TICK_CONFIG.get(threshold)!;
            break;
          }
        }

        // For 24-hour steps (daily increments), align to 9:30 AM ET
        const nextTime = currentTime + stepHours * MILLISECONDS_PER_HOUR;

        // Don't go past expiration
        if (nextTime >= earliestLegExpiration) {
          break;
        }

        // If we're within 1 hour of expiration, skip to expiration
        // since it's not supported by the pnl algorithm
        const timeToExpiration = earliestLegExpiration - nextTime;
        if (timeToExpiration <= MILLISECONDS_PER_HOUR) {
          break;
        }

        marksArray.push(nextTime);
        currentTime = nextTime;
      }

      // Always end exactly at expiration
      if (marksArray[marksArray.length - 1] !== earliestLegExpiration) {
        marksArray.push(earliestLegExpiration);
      }

      return marksArray.map((value) => ({ value }));
    }, [earliestLegExpiration, nowET]);

    const displayValueFormatter = useCallback(
      (value: number | number[]) => {
        if (Array.isArray(value)) {
          const startDisplay = formatPnlTteDateDisplay(
            value[0],
            earliestLegExpiration,
            DEFAULT_DTE_PNL_TICK_CONFIG,
          );
          const endDisplay = formatPnlTteDateDisplay(
            value[1],
            earliestLegExpiration,
            DEFAULT_DTE_PNL_TICK_CONFIG,
          );
          return `${startDisplay} - ${endDisplay}`;
        }

        return formatPnlTteDateDisplay(
          value,
          earliestLegExpiration,
          DEFAULT_DTE_PNL_TICK_CONFIG,
        );
      },
      [earliestLegExpiration],
    );

    return (
      legs.length > 0 && (
        <Box
          sx={{
            p: '8px 12px',
            mt: 2,
            ml: 'auto',
            width: '35%',
            minWidth: 'max-content',
            maxWidth: 500,
            bgcolor: (theme) => alpha(theme.palette.text.primary, 0.04),
            borderRadius: '8px',
            '& .MuiTypography-root': { whiteSpace: 'nowrap' },
          }}
        >
          <SliderFilter
            header="Time to Expiration"
            name="date"
            min={nowET.valueOf()}
            max={earliestLegExpiration}
            marks={marks}
            step={null}
            value={baseDate}
            onChange={(newValue) => {
              updateBaseDate(newValue as number);
            }}
            valueFormat="date"
            displayValueFormatter={displayValueFormatter}
            updateOnSlide
          />
        </Box>
      )
    );
  },
);

BaseDateSettings.displayName = 'BaseDateSettings';

export default BaseDateSettings;
